import { List } from 'immutable'
import React, { ReactElement } from 'react'

import Company from '../../../model/company'
import Contract from '../../../model/contract'
import Employee, { AffiliationType } from '../../../model/employee'
import LeaveType, { LeaveTypeName } from '../../../model/leaveType'
import { LeaveDefinitionCreationFields, SupplementDefinitionCreationFields } from '../../../model/remuneration'
import SalaryCycle from '../../../model/salaryCycle'
import SupplementType, { SupplementTypeName } from '../../../model/supplementType'
import { ContractReducer } from '../../../reducers/contracts'
import { EmployeeReducer } from '../../../reducers/employees'
import DayLaborer from '../../../types/day-laborer'
import { formatDate, getDate, isSameOrAfter, isTimeAfter } from '../../../utils/date-utils'
import { getEmployeePayType } from '../../../utils/employee-utils'
import { FormComponentProps, withValidations } from '../../../utils/form-utils'
import { forceParseInputNumber, formatInputNumber, parseInputNumber } from '../../../utils/number-utils'
import { setByPath } from '../../../utils/object-utils'
import { getCurrentPeriod } from '../../../utils/salary-period-utils'
import { t } from '../../../utils/translation-utils'
import VacationForm from '../../contracts-add/VacationForm'
import Button from '../../elements/button'
import Col from '../../elements/grid/col'
import Row from '../../elements/grid/row'
import LoadingOverlay from '../../widgets/LoadingOverlay'

type Fields = {
  vacationType: 'paid' | 'unpaid' | 'none'
  vacationDays: string
  vacationSupplement: string
  vacationMoney: string
  extraVacationDays: string
  personalDays: string
  hasExtraVacation: boolean
  hasPersonalDays: boolean
  hasVacationFund: boolean
  vacationFundCVR?: string
  manualVacationFund: boolean
  horestaVacationSupplement: boolean
  horestaVacationSupplementAmount?: string
  originalExtraVacationAccrual: 'Monthly' | 'Yearly'
  originalPersonalDaysAccrual: 'Monthly' | 'Yearly'
  extraVacationAccrual: 'Monthly' | 'Yearly'
  personalDaysAccrual: 'Monthly' | 'Yearly'
  timeBank: 'None' | 'Flex' | 'Overtime'
  overtimeRate?: string
  useATP: boolean
  useATPInitial: boolean
  useDayLaborer: boolean
  dayLaborer: DayLaborer
  minDays: number
  maxDays: number
  greatPrayerDaySupplement: boolean
  useGPDInitial: boolean
  includeVacationSupplementsInPensionBasis: boolean
}

export type VacationResult = {
  affiliationType: AffiliationType
  vacationFundCVR?: string
  manualVacationFund: boolean
  overtimeRate?: number
  useATP: boolean
  dayLaborer: DayLaborer
  remuneration: {
    leave: LeaveDefinitionCreationFields[]
    supplements: SupplementDefinitionCreationFields[]
  }
}

type Props = {
  mutableContract: Contract
  validFrom: Date
  company: Company
  employee: Employee
  contracts: ContractReducer
  employees: EmployeeReducer
  salaryCycle: SalaryCycle
  leaveTypes: List<LeaveType>
  supplementTypes: List<SupplementType>
}

function EmploymentVacationEditForm(props: Props & FormComponentProps<Fields, VacationResult>): ReactElement | null {
  const contract = props.mutableContract

  return (
    <div>
      {props.getFormError()}
      <VacationForm<Fields>
        {...props}
        payType={getEmployeePayType(contract)}
        company={props.company}
        decorateField={props.decorateField}
        getFieldValue={props.getFieldValue}
      />
      <Row>
        <Col span={24}>
          <Button htmlType="submit" size="extra-extra-large" type="primary">
            {t('form.button.save_changes')}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col span={24} style={{ textAlign: 'center' }}>
          <div className="ant-form-warning">
            {isTimeAfter(props.validFrom, getDate())
              ? t('employment_vacation.card.edit.warning.at_date', { date: formatDate(props.validFrom) })
              : t('employment_vacation.card.edit.warning.immediate')}
          </div>
        </Col>
      </Row>
      {(props.contracts.saving || props.employees.saving) && <LoadingOverlay />}
    </div>
  )
}

export default withValidations<Props, Fields, VacationResult>({
  mapPropsToFields: (props: Props): Fields => {
    const fields: Fields = {
      vacationType: 'none',
      vacationDays: formatInputNumber(25),
      vacationSupplement: formatInputNumber(1.0),
      vacationMoney: formatInputNumber(12.5),
      extraVacationDays: formatInputNumber(0),
      personalDays: formatInputNumber(0),
      hasVacationFund: props.mutableContract.vacationFundCVR ? true : false,
      vacationFundCVR: props.mutableContract.vacationFundCVR,
      horestaVacationSupplement: false,
      extraVacationAccrual: 'Yearly',
      personalDaysAccrual: 'Yearly',
      hasExtraVacation: false,
      hasPersonalDays: false,
      originalExtraVacationAccrual: 'Yearly',
      originalPersonalDaysAccrual: 'Yearly',
      timeBank: 'None',
      useATP: true,
      useATPInitial: true,
      manualVacationFund: false,
      useDayLaborer: props.mutableContract.dayLaborer && props.mutableContract.dayLaborer !== DayLaborer.NONE,
      dayLaborer: props.mutableContract.dayLaborer || DayLaborer.NONE,
      minDays: 25,
      maxDays: 25,
      greatPrayerDaySupplement: false,
      useGPDInitial: false,
      includeVacationSupplementsInPensionBasis: false,
    }
    if (
      props.mutableContract.remuneration &&
      (props.mutableContract.remuneration.leave.length > 0 || props.mutableContract.remuneration.supplements.length > 0)
    ) {
      props.mutableContract.remuneration.leave.forEach((row) => {
        const leaveType = props.leaveTypes.find((type) => type.id === row.typeID)
        if (!leaveType) {
          return
        }

        switch (leaveType.name) {
          case 'DenmarkVacationPaid':
          case 'DenmarkVacationAccrual':
            fields.vacationType = 'paid'
            fields.vacationDays = formatInputNumber(row.days)
            fields.minDays = leaveType.minDays
            fields.maxDays = leaveType.maxDays
            if (row.rate !== undefined) {
              fields.vacationMoney = formatInputNumber(row.rate * 100)
            }
            break
          case 'DenmarkVacationNoPay':
          case 'DenmarkVacationFund':
          case 'DenmarkVacationFundWithPension':
            fields.vacationType = 'unpaid'
            fields.vacationDays = formatInputNumber(row.days)
            fields.minDays = leaveType.minDays
            fields.maxDays = leaveType.maxDays
            if (row.rate !== undefined) {
              fields.vacationMoney = formatInputNumber(row.rate * 100)
            }
            break
          case 'DenmarkOptionalVacation':
            fields.extraVacationDays = formatInputNumber(row.days)
            fields.hasExtraVacation = true
            fields.extraVacationAccrual = 'Yearly'
            fields.originalExtraVacationAccrual = 'Yearly'
            break
          case 'DenmarkPersonalDay':
            fields.personalDays = formatInputNumber(row.days)
            fields.hasPersonalDays = true
            fields.personalDaysAccrual = 'Yearly'
            fields.originalPersonalDaysAccrual = 'Yearly'
            break
          case 'DenmarkExtraVacationAccrual':
            fields.extraVacationDays = formatInputNumber(row.days)
            fields.hasExtraVacation = true
            fields.extraVacationAccrual = 'Monthly'
            fields.originalExtraVacationAccrual = 'Monthly'
            break
          case 'DenmarkPersonalTimeAccrual':
            fields.personalDays = formatInputNumber(row.days)
            fields.hasPersonalDays = true
            fields.personalDaysAccrual = 'Monthly'
            fields.originalPersonalDaysAccrual = 'Monthly'
            break
          case 'DenmarkFlexTime':
            fields.timeBank = 'Flex'
            break
          case 'DenmarkOvertime':
            fields.timeBank = 'Overtime'
            fields.overtimeRate = formatInputNumber(row.rate || 0)
            break
          default:
            break
        }
      })
      props.mutableContract.remuneration.supplements.forEach((row) => {
        const supplementType = props.supplementTypes.find((type) => type.id === row.typeID)
        if (!supplementType) {
          return
        }
        switch (supplementType.name) {
          case 'DenmarkVacationPaidSupplement':
          case 'DenmarkVacationSupplementPension':
          case 'DenmarkVacationSupplement':
            fields.vacationSupplement = formatInputNumber(row.compensationRate * 100)
            if (supplementType.name === 'DenmarkVacationSupplementPension') {
              fields.includeVacationSupplementsInPensionBasis = true
            }
            break
          case 'DenmarkVacationSupplementSpecial':
            fields.horestaVacationSupplement = true
            fields.horestaVacationSupplementAmount = formatInputNumber(row.compensationRate * 100)
            break
          case 'DenmarkGreatPrayerDaySupplement':
            fields.greatPrayerDaySupplement = true
            break
          default:
            break
        }
      })
      fields.manualVacationFund = props.mutableContract.manualVacationFund
    }
    if (fields.vacationType === 'none' && props.mutableContract.remuneration) {
      fields.useATP = props.mutableContract.remuneration.pension.some((pension) => pension.scheme === 'ATP')
      fields.useATPInitial = fields.useATP
    }
    fields.useGPDInitial = fields.greatPrayerDaySupplement
    return fields
  },
  onChange: (key: string, val: any, allValues: Fields, options: Record<string, unknown>): Partial<Fields> => {
    const values: Partial<Fields> = {}
    switch (key) {
      case 'vacationDays':
      case 'vacationSupplement':
      case 'vacationMoney':
      case 'extraVacationDays':
      case 'personalDays':
        setByPath(values, key, formatInputNumber(parseInputNumber(val, { trim: options.trigger === 'onBlur' })))
        break
      case 'vacationType':
        setByPath(values, key, val)
        if (val === 'none') {
          setByPath(values, 'greatPrayerDaySupplement', false)
        } else {
          setByPath(values, 'greatPrayerDaySupplement', allValues.useGPDInitial)
        }
        break
      default:
        setByPath(values, key, val)
        break
    }
    return values
  },
  onSubmit: (values: Fields, props: Props) => {
    const period = getCurrentPeriod(props.salaryCycle)
    const hadIncludeInPension =
      props.mutableContract.remuneration &&
      props.mutableContract.remuneration.leave.some(
        (row) => !!row.type && row.type.name === 'DenmarkVacationFundWithPension'
      )
    const getLeaveType = (name: LeaveTypeName) => props.leaveTypes.find((type) => type.name === name)!
    const getSupplementType = (name: SupplementTypeName) => props.supplementTypes.find((type) => type.name === name)!

    const allowVacationRate = props.company.settingsEnabled.some((setting) => setting === 'AllowVacationRate')

    let affiliationType: AffiliationType = 'Standard'
    const leave: LeaveDefinitionCreationFields[] = []
    const supplements: SupplementDefinitionCreationFields[] = []
    if (values.vacationType !== 'none') {
      if (isSameOrAfter(getDate(period.dispositionDate), getDate('2020-09-01'))) {
        const unpaidRate =
          forceParseInputNumber(values.vacationMoney) !== 12.5
            ? forceParseInputNumber(values.vacationMoney) / 100
            : undefined

        const paidRate = allowVacationRate
          ? forceParseInputNumber(values.vacationMoney) !== 12.5
            ? forceParseInputNumber(values.vacationMoney) / 100
            : undefined
          : undefined

        if (values.vacationType === 'paid') {
          leave.push({
            typeID: getLeaveType('DenmarkVacationAccrual').id,
            days: forceParseInputNumber(values.vacationDays),
            rate: paidRate,
          })
          if (values.includeVacationSupplementsInPensionBasis) {
            supplements.push({
              typeID: getSupplementType('DenmarkVacationSupplementPension').id,
              compensationRate: forceParseInputNumber(values.vacationSupplement) / 100,
            })
          } else {
            supplements.push({
              typeID: getSupplementType('DenmarkVacationSupplement').id,
              compensationRate: forceParseInputNumber(values.vacationSupplement) / 100,
            })
          }
        } else {
          if (hadIncludeInPension) {
            leave.push({
              typeID: getLeaveType('DenmarkVacationFundWithPension').id,
              days: forceParseInputNumber(values.vacationDays),
              rate: unpaidRate,
            })
          } else {
            leave.push({
              typeID: getLeaveType('DenmarkVacationFund').id,
              days: forceParseInputNumber(values.vacationDays),
              rate: unpaidRate,
            })
          }
        }
      } else {
        if (values.vacationType === 'paid') {
          leave.push({
            typeID: getLeaveType('DenmarkVacationPaid').id,
            days: forceParseInputNumber(values.vacationDays),
          })
          supplements.push({
            typeID: getSupplementType('DenmarkVacationPaidSupplement').id,
            compensationRate: forceParseInputNumber(values.vacationSupplement) / 100,
          })
        } else {
          if (hadIncludeInPension) {
            leave.push({
              typeID: getLeaveType('DenmarkVacationFundWithPension').id,
              days: forceParseInputNumber(values.vacationDays),
            })
          } else {
            leave.push({
              typeID: getLeaveType('DenmarkVacationNoPay').id,
              days: forceParseInputNumber(values.vacationDays),
            })
          }
        }
      }
      if (forceParseInputNumber(values.extraVacationDays) > 0) {
        if (values.extraVacationAccrual === 'Yearly') {
          leave.push({
            typeID: getLeaveType('DenmarkOptionalVacation').id,
            days: forceParseInputNumber(values.extraVacationDays),
          })
        } else {
          leave.push({
            typeID: getLeaveType('DenmarkExtraVacationAccrual').id,
            days: forceParseInputNumber(values.extraVacationDays),
          })
        }
      }
      if (forceParseInputNumber(values.personalDays) > 0) {
        if (values.personalDaysAccrual === 'Yearly') {
          leave.push({
            typeID: getLeaveType('DenmarkPersonalDay').id,
            days: forceParseInputNumber(values.personalDays),
          })
        } else {
          leave.push({
            typeID: getLeaveType('DenmarkPersonalTimeAccrual').id,
            days: forceParseInputNumber(values.personalDays),
          })
        }
      }
    } else {
      affiliationType = 'Director'
    }
    if (values.horestaVacationSupplement) {
      supplements.push({
        typeID: getSupplementType('DenmarkVacationSupplementSpecial').id,
        compensationRate: forceParseInputNumber(values.horestaVacationSupplementAmount) / 100,
      })
    }
    if (values.timeBank === 'Flex') {
      leave.push({
        typeID: getLeaveType('DenmarkFlexTime').id,
        days: 0,
      })
    }
    if (values.timeBank === 'Overtime') {
      leave.push({
        typeID: getLeaveType('DenmarkOvertime').id,
        days: 0,
        rate: forceParseInputNumber(values.overtimeRate),
      })
    }
    if (values.greatPrayerDaySupplement) {
      supplements.push({
        typeID: getSupplementType('DenmarkGreatPrayerDaySupplement').id,
        compensationRate: 0.0045,
      })
    }
    return {
      affiliationType,
      vacationFundCVR: values.hasVacationFund ? values.vacationFundCVR : undefined,
      manualVacationFund: values.manualVacationFund,
      useATP: values.vacationType === 'none' ? values.useATP : true,
      remuneration: {
        leave,
        supplements,
      },
      dayLaborer: values.vacationType === 'unpaid' ? values.dayLaborer : DayLaborer.NONE,
    }
  },
})(EmploymentVacationEditForm)
